import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Tooltip,
  Circle,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import { Bus, Navigation, MapPinned } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { API_BASE as API, BACKEND_URL } from '@/config/api';
import { getMapColorsForRecorrido } from '@/data/recorridos/config';
import { geolocationErrorMessage } from '@/lib/geolocation';
import { MENSAJE_BUS_REEMPLAZO_SIN_TIEMPO_REAL } from '@/lib/paraderoRecorrido';
import { cn } from '@/lib/utils';
import { parseKmlAllLineStringsToLeaflet } from '@/utils/kmlLineString';
import t1Paradas from '@/data/paradas/t1Paradas.json';
import t2Paradas from '@/data/paradas/t2Paradas.json';
import t3Paradas from '@/data/paradas/t3Paradas.json';
import t4Paradas from '@/data/paradas/t4Paradas.json';
import t5aParadas from '@/data/paradas/t5aParadas.json';
import t5bParadas from '@/data/paradas/t5bParadas.json';
import busTagImg from '@/assets/map/bus-tag.png';
import paradasImg from '@/assets/map/paradas.png';

const PUBLIC_BASE = process.env.PUBLIC_URL || '';

const _liveEnv = (process.env.REACT_APP_ENABLE_LIVE_BUS_LOCATION || '').trim().toLowerCase();
/**
 * Polling de buses: por defecto activo si no se desactiva con REACT_APP_ENABLE_LIVE_BUS_LOCATION.
 * `API` es siempre `/api` o URL absoluta si hay REACT_APP_BACKEND_URL (proxy en dev / Vercel).
 */
const ENABLE_LIVE_BUS_LOCATION =
  Boolean((API || '').trim()) &&
  _liveEnv !== 'false' &&
  _liveEnv !== '0' &&
  _liveEnv !== 'off';

/** Refresco GET /api/buses/near (ms). Env: REACT_APP_LIVE_BUS_POLL_MS; default 2000; mín. 1000. Alinear con `GPS_POLL_SECONDS` en Railway. */
const LIVE_BUSES_POLL_MS = (() => {
  const raw = (process.env.REACT_APP_LIVE_BUS_POLL_MS || '').trim();
  const n = parseInt(raw, 10);
  const ms = Number.isNaN(n) ? 2000 : n;
  return Math.min(60000, Math.max(1000, ms));
})();

/**
 * divIcon + <img>: más robusto que L.icon(PNG) con Tailwind/Leaflet (sombras y max-width en img).
 * `src` viene del import webpack (URL absoluta al bundle).
 */
const createMapImageIcon = (src, size) => {
  const ax = Math.round(size / 2);
  const shadow = 'drop-shadow(0 2px 6px rgba(0,0,0,0.28))';
  return L.divIcon({
    className: 'munibus-map-icon munibus-map-icon--img',
    html: `<img src="${src}" alt="" width="${size}" height="${size}" decoding="async" style="display:block;width:${size}px;height:${size}px;max-width:none;object-fit:contain;pointer-events:none;filter:${shadow}" />`,
    iconSize: [size, size],
    iconAnchor: [ax, size],
    popupAnchor: [0, -Math.round(size * 0.85)],
  });
};

const busIcon = createMapImageIcon(busTagImg, 44);
const stopIcon = createMapImageIcon(paradasImg, 38);
const stopIconSelected = createMapImageIcon(paradasImg, 44);

const createUserDotIcon = (color, size = 20) =>
  L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; border: 3px solid white; border-radius: 50%; width: ${size}px; height: ${size}px; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

const userIcon = createUserDotIcon('hsl(4, 90%, 58%)', 20);

function buildBounds(routePositions, stop, extraBusPositions, user, extraPolylines, extraStopPositions) {
  if (!stop) return null;
  const b = L.latLngBounds(stop, stop);
  if (routePositions?.length) {
    routePositions.forEach((p) => b.extend(p));
  }
  (extraBusPositions || []).forEach((pos) => {
    if (Array.isArray(pos) && pos.length >= 2) b.extend(pos);
  });
  if (user) b.extend(user);
  (extraPolylines || []).forEach((ring) => {
    if (ring?.length) ring.forEach((p) => b.extend(p));
  });
  (extraStopPositions || []).forEach((pos) => {
    if (Array.isArray(pos) && pos.length >= 2) b.extend(pos);
  });
  return b;
}

/**
 * Encuadre para «Buscar Bus»: si hay GPS del bus, solo bus (+ usuario); si no, paradero (+ usuario).
 * No mezcla bus lejano con paradero (evita fit que muestra todo el recorrido T1).
 */
function buildBusSearchBounds(stop, extraBusPositions, user) {
  const buses = (extraBusPositions || []).filter((pos) => Array.isArray(pos) && pos.length >= 2);
  if (buses.length > 0) {
    const b = L.latLngBounds(buses[0], buses[0]);
    buses.forEach((p) => b.extend(p));
    if (user) b.extend(user);
    return b;
  }
  if (!stop) return null;
  const b = L.latLngBounds(stop, stop);
  if (user) b.extend(user);
  return b;
}

function isBoundsPointOnly(bounds) {
  if (!bounds?.isValid()) return true;
  const pad = 1e-8;
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  return Math.abs(sw.lat - ne.lat) < pad && Math.abs(sw.lng - ne.lng) < pad;
}

/** Encuadre más cerrado que antes (antes 40 / zoom 16). */
const FIT_PADDING = [22, 22];
const FIT_MAX_ZOOM = 18;
/** Zoom al activar «Mi ubicación» (primera posición GPS). */
const USER_TRACK_ZOOM = 18;

/**
 * Encuadre inicial al cambiar recorrido/sentido/KML — solo cuando `routeFitKey` cambia.
 * No depender de referencias de arrays (extraPolylines/extraStopPositions) ni del poll de predicción:
 * si no, cada render re-ejecuta fitBounds y el usuario pierde el zoom manual.
 */
function AutoFitRouteOnParaderoChange({
  routeFitKey,
  routePositions,
  stop,
  extraPolylines,
  extraStopPositions,
}) {
  const map = useMap();

  useEffect(() => {
    const bounds = buildBounds(routePositions, stop, null, null, extraPolylines, extraStopPositions);
    if (!bounds?.isValid()) return;
    map.fitBounds(bounds, {
      padding: FIT_PADDING,
      maxZoom: FIT_MAX_ZOOM,
      animate: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo routeFitKey; evita refit al poll / refs de arrays
  }, [map, routeFitKey]);

  return null;
}

function ManualRecenterFit({ stop, extraBusPositions, user, recenterToken }) {
  const map = useMap();
  const lastApplied = useRef(0);

  useEffect(() => {
    if (recenterToken === 0) {
      lastApplied.current = 0;
      return;
    }
    if (recenterToken === lastApplied.current) return;
    lastApplied.current = recenterToken;

    const bounds = buildBusSearchBounds(stop, extraBusPositions, user);
    if (!bounds?.isValid()) return;

    if (isBoundsPointOnly(bounds)) {
      map.setView(bounds.getCenter(), FIT_MAX_ZOOM, { animate: true });
      return;
    }
    map.fitBounds(bounds, {
      padding: [48, 48],
      maxZoom: FIT_MAX_ZOOM,
      animate: true,
    });
  }, [map, recenterToken, stop, extraBusPositions, user]);

  return null;
}

/** Acerca el mapa a la primera posición al encender el seguimiento GPS. */
function FlyToUserOnFirstFix({ userLocation, trackingUser }) {
  const map = useMap();
  const hasFlownRef = useRef(false);

  useEffect(() => {
    if (!trackingUser) {
      hasFlownRef.current = false;
      return;
    }
    if (!userLocation?.length) return;
    if (hasFlownRef.current) return;
    hasFlownRef.current = true;
    map.flyTo(userLocation, USER_TRACK_ZOOM, { duration: 0.55 });
  }, [map, trackingUser, userLocation]);

  return null;
}

function InvalidateMapSize() {
  const map = useMap();
  useEffect(() => {
    const run = () => map.invalidateSize();
    const t0 = requestAnimationFrame(run);
    const t1 = setTimeout(run, 200);
    const t2 = setTimeout(run, 600);
    return () => {
      cancelAnimationFrame(t0);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [map]);
  return null;
}

export default function BusMap({
  /** Cabecera ida del recorrido (centro, radios /buses/near); no implica «paradero» fijo del usuario */
  mapAnchor,
  prediccion,
  recorrido,
  sentido,
  onSentidoChange,
  /** Si viene del backoffice (patente asignada al trayecto), tiene prioridad sobre config estática */
  busFilterId = null,
  /** Recorrido desactivado en admin: capa oscura sobre el mapa y mensaje de bus externo. */
  servicioReemplazoExterno = false,
  /** Resalta la parada cuyo nombre coincide con la última pasada (GPS) */
  highlightStopName = null,
}) {
  /** Posiciones en vivo desde GET /api/buses/near (Link Sur). */
  const [liveBuses, setLiveBuses] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [userAccuracyM, setUserAccuracyM] = useState(null);
  const [geoError, setGeoError] = useState(null);
  const [trackingUser, setTrackingUser] = useState(false);
  const [recenterToken, setRecenterToken] = useState(0);
  const [kmlPositions, setKmlPositions] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const staticRoutePositions = useMemo(() => [], []);
  const [kmlIvError, setKmlIvError] = useState(null);
  const [kmlIvLoading, setKmlIvLoading] = useState(false);
  const intervalRef = useRef(null);
  const geoWatchIdRef = useRef(null);

  const variantOptions = useMemo(
    () => recorrido?.kmlVariantesBySentido?.[sentido] || [],
    [recorrido, sentido]
  );

  useEffect(() => {
    if (!variantOptions.length) {
      setSelectedVariantId(null);
      return;
    }
    const hasSelected = variantOptions.some((v) => v.id === selectedVariantId);
    if (!hasSelected) {
      setSelectedVariantId(variantOptions[0].id);
    }
  }, [variantOptions, selectedVariantId]);

  const activeKmlPath = useMemo(() => {
    if (variantOptions.length > 0) {
      const chosen = variantOptions.find((v) => v.id === selectedVariantId) || variantOptions[0];
      return chosen?.path || null;
    }
    return recorrido?.kmlBySentido?.[sentido] || recorrido?.kmlPublicPath || null;
  }, [variantOptions, selectedVariantId, recorrido, sentido]);

  const kmlFullUrl = useMemo(() => {
    if (!activeKmlPath) return null;
    return `${PUBLIC_BASE}${activeKmlPath}`;
  }, [activeKmlPath]);

  const hasKml = Boolean(kmlFullUrl);

  useEffect(() => {
    setRecenterToken(0);
    setKmlPositions(null);
    setKmlIvError(null);
  }, [mapAnchor.id, recorrido.id, sentido, selectedVariantId]);

  useEffect(() => {
    if (!kmlFullUrl) {
      setKmlIvLoading(false);
      setKmlIvError(null);
      setKmlPositions(null);
      return undefined;
    }

    let cancelled = false;
    setKmlIvLoading(true);
    setKmlIvError(null);

    fetch(kmlFullUrl)
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then((text) => {
        const rings = parseKmlAllLineStringsToLeaflet(text);
        if (rings.length === 0) {
          throw new Error('No se encontraron LineString en el KML.');
        }
        let firstRing = rings[0];
        // KML «ida» a veces viene cabecera destino → Paine; el itinerario oficial es Paine → destino (T1 Rangue, T2 Los Arrieros)
        if (/\/(t1|t2|t3|t4|t5a|t5b)_ida\.kml/i.test(kmlFullUrl) && firstRing.length > 1) {
          firstRing = [...firstRing].reverse();
        }
        if (!cancelled) {
          setKmlPositions(firstRing);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setKmlIvError('No se pudo cargar el trazado del recorrido.');
          setKmlPositions(null);
        }
      })
      .finally(() => {
        if (!cancelled) setKmlIvLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [kmlFullUrl]);

  const activeKmlRing = kmlPositions;

  const extraPolylinesForFit = useMemo(() => {
    const list = [];
    if (hasKml && activeKmlRing?.length) list.push(activeKmlRing);
    return list;
  }, [hasKml, activeKmlRing]);

  const isT1Recorrido = recorrido.id === 't1a' || recorrido.id === 't1b';
  const isT2Recorrido = recorrido.id === 't2';
  const isT3Recorrido = recorrido.id === 't3';
  const isT4Recorrido = recorrido.id === 't4';
  const isT5Recorrido = recorrido.id === 't5a' || recorrido.id === 't5b';
  const isCatalogRecorrido =
    isT1Recorrido || isT2Recorrido || isT3Recorrido || isT4Recorrido || isT5Recorrido;

  /** T1–T5: mismos JSON que el itinerario — todos los pins en el mapa por sentido. */
  const paradasCatalogList = useMemo(() => {
    if (isT1Recorrido) {
      const col = sentido === 'vuelta' ? t1Paradas.vuelta : t1Paradas.ida;
      return Array.isArray(col) ? col : [];
    }
    if (isT2Recorrido) {
      const col = sentido === 'vuelta' ? t2Paradas.vuelta : t2Paradas.ida;
      return Array.isArray(col) ? col : [];
    }
    if (isT3Recorrido) {
      const col = sentido === 'vuelta' ? t3Paradas.vuelta : t3Paradas.ida;
      return Array.isArray(col) ? col : [];
    }
    if (isT4Recorrido) {
      const col = sentido === 'vuelta' ? t4Paradas.vuelta : t4Paradas.ida;
      return Array.isArray(col) ? col : [];
    }
    if (recorrido.id === 't5a') {
      const col = sentido === 'vuelta' ? t5aParadas.vuelta : t5aParadas.ida;
      return Array.isArray(col) ? col : [];
    }
    if (recorrido.id === 't5b') {
      const col = sentido === 'vuelta' ? t5bParadas.vuelta : t5bParadas.ida;
      return Array.isArray(col) ? col : [];
    }
    return [];
  }, [recorrido.id, isT1Recorrido, isT2Recorrido, isT3Recorrido, isT4Recorrido, sentido]);

  const paradasPositionsForFit = useMemo(
    () => paradasCatalogList.map((p) => [p.lat, p.lng]),
    [paradasCatalogList]
  );

  /** Solo cuando cambia recorrido/sentido/variante/KML — no en cada render ni poll de predicción. */
  const routeFitKey = useMemo(
    () =>
      [
        mapAnchor.id,
        recorrido.id,
        sentido,
        String(selectedVariantId ?? ''),
        String(activeKmlRing?.length ?? 0),
      ].join('|'),
    [mapAnchor.id, recorrido.id, sentido, selectedVariantId, activeKmlRing?.length]
  );

  const anchorEnListaCatalogo = useMemo(
    () => paradasCatalogList.some((p) => p.id === mapAnchor.id),
    [paradasCatalogList, mapAnchor.id]
  );

  /** Si hay catálogo y la cabecera está en la lista, solo esos pins (sin duplicar el ancla). */
  const showSingleFallbackMarker =
    paradasCatalogList.length === 0 || !anchorEnListaCatalogo;

  const stopPosition = useMemo(() => [mapAnchor.lat, mapAnchor.lng], [mapAnchor.lat, mapAnchor.lng]);

  const highlightNorm = (highlightStopName || '').trim().toLowerCase();
  const mapCenter = stopPosition;

  const extraBusPositionsForFit = useMemo(
    () => liveBuses.map((b) => [b.lat, b.lng]),
    [liveBuses]
  );

  /** Patente del tramo: asignación backoffice o fallback del catálogo. */
  const targetBusPatente = useMemo(
    () => (busFilterId ?? recorrido.busId ?? recorrido.patente ?? '').trim(),
    [busFilterId, recorrido.busId, recorrido.patente]
  );

  /** T1–T5A: el bus del tramo puede estar lejos del paradero. */
  const nearRadiusKm = isCatalogRecorrido ? 60 : 32;

  const fetchLiveBuses = useCallback(async () => {
    if (!ENABLE_LIVE_BUS_LOCATION) {
      setLiveBuses([]);
      return;
    }
    if (!targetBusPatente) {
      setLiveBuses([]);
      return;
    }
    try {
      const params = {
        lat: mapAnchor.lat,
        lng: mapAnchor.lng,
        radius_km: nearRadiusKm,
        bus_id: targetBusPatente,
      };
      const response = await axios.get(`${API}/buses/near`, {
        params: { ...params, _t: Date.now() },
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setLiveBuses(list);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching bus locations:', error);
      }
      setLiveBuses([]);
    }
  }, [mapAnchor.lat, mapAnchor.lng, nearRadiusKm, targetBusPatente]);

  useEffect(() => {
    if (!ENABLE_LIVE_BUS_LOCATION) {
      setLiveBuses([]);
      return undefined;
    }
    fetchLiveBuses();
    intervalRef.current = setInterval(fetchLiveBuses, LIVE_BUSES_POLL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchLiveBuses]);

  const stopGeoWatch = useCallback(() => {
    if (geoWatchIdRef.current != null) {
      navigator.geolocation.clearWatch(geoWatchIdRef.current);
      geoWatchIdRef.current = null;
    }
    setTrackingUser(false);
  }, []);

  useEffect(() => () => stopGeoWatch(), [stopGeoWatch]);

  const startUserTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError('La geolocalización no está disponible en este dispositivo.');
      return;
    }
    const secureOk = window.isSecureContext || window.location.hostname === 'localhost';
    if (!secureOk) {
      setGeoError(
        'Tu ubicación solo funciona en HTTPS o en http://localhost. Abre el sitio con una URL segura.'
      );
      return;
    }

    setGeoError(null);
    stopGeoWatch();

    geoWatchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setUserLocation([pos.coords.latitude, pos.coords.longitude]);
        setUserAccuracyM(pos.coords.accuracy ?? null);
        setGeoError(null);
      },
      (err) => {
        setGeoError(geolocationErrorMessage(err));
      },
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 25000,
      }
    );
    setTrackingUser(true);
  }, [stopGeoWatch]);

  const handleToggleLocation = () => {
    if (trackingUser) {
      stopGeoWatch();
      setUserLocation(null);
      setUserAccuracyM(null);
      setGeoError(null);
      return;
    }
    startUserTracking();
  };

  const handleRecenterMap = () => {
    setRecenterToken((t) => t + 1);
  };

  const mapLineColors = useMemo(() => getMapColorsForRecorrido(recorrido), [recorrido]);
  const lineColor = sentido === 'ida' ? mapLineColors.ida : mapLineColors.vuelta;
  const sentidoLabel = sentido === 'ida' ? 'Ida' : 'Vuelta';

  return (
    <Card
      className="overflow-hidden rounded-3xl border-0 bg-card shadow-card"
      data-testid="bus-map"
    >
      <CardContent className="p-0">
        <div
          className="relative z-0 aspect-[4/3] min-h-[240px] overflow-hidden sm:min-h-[280px]"
          data-testid="map-container"
        >
          <div className="pointer-events-none absolute inset-x-0 top-0 z-[1000] flex flex-col items-stretch gap-2 p-2 sm:p-3">
            <div className="pointer-events-auto flex flex-wrap justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleRecenterMap}
                className="touch-target border border-border/60 bg-card/95 shadow-md backdrop-blur-sm"
                data-testid="bus-search-map-button"
                type="button"
                aria-label="Buscar bus en el mapa"
              >
                <Bus className="mr-1 h-4 w-4 shrink-0" aria-hidden />
                Buscar Bus
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleToggleLocation}
                className={cn(
                  'touch-target border shadow-md backdrop-blur-sm',
                  trackingUser
                    ? 'border-primary/70 bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border-border/60 bg-card/95 text-foreground'
                )}
                data-testid="activate-location-button"
                type="button"
              >
                {trackingUser ? (
                  <>
                    <MapPinned className="mr-1 h-4 w-4" />
                    Quitar GPS
                  </>
                ) : (
                  <>
                    <Navigation className="mr-1 h-4 w-4" />
                    Mi ubicación
                  </>
                )}
              </Button>
            </div>
            {geoError && (
              <p className="pointer-events-auto rounded-lg border border-destructive/30 bg-card/95 px-3 py-2 text-xs text-destructive shadow-md backdrop-blur-sm">
                {geoError}
              </p>
            )}
          </div>
          <MapContainer
            key={`${mapAnchor.id}-${recorrido.id}`}
            center={mapCenter}
            zoom={15}
            scrollWheelZoom
            style={{ height: '100%', width: '100%' }}
            zoomControl
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <InvalidateMapSize />
            <AutoFitRouteOnParaderoChange
              routeFitKey={routeFitKey}
              routePositions={staticRoutePositions}
              stop={stopPosition}
              extraPolylines={extraPolylinesForFit}
              extraStopPositions={paradasPositionsForFit}
            />
            <ManualRecenterFit
              stop={stopPosition}
              extraBusPositions={extraBusPositionsForFit}
              user={userLocation}
              recenterToken={recenterToken}
            />
            <FlyToUserOnFirstFix userLocation={userLocation} trackingUser={trackingUser} />

            {hasKml && activeKmlRing && activeKmlRing.length > 0 && (
              <Polyline
                pathOptions={{
                  color: lineColor,
                  weight: sentido === 'vuelta' ? 5 : 4,
                  opacity: 0.92,
                  lineCap: 'round',
                  lineJoin: 'round',
                }}
                positions={activeKmlRing}
              >
                <Tooltip sticky direction="top" opacity={0.95}>
                  {recorrido.nombre} · {sentidoLabel}
                </Tooltip>
              </Polyline>
            )}

            {isCatalogRecorrido &&
              paradasCatalogList.length > 0 &&
              paradasCatalogList.map((p) => {
                const selected =
                  Boolean(highlightNorm) &&
                  (p.nombre || '').trim().toLowerCase() === highlightNorm;
                return (
                  <Marker
                    key={p.id}
                    position={[p.lat, p.lng]}
                    zIndexOffset={selected ? 750 : 400}
                    icon={selected ? stopIconSelected : stopIcon}
                  >
                    <Tooltip direction="top" opacity={0.92} offset={[0, -6]}>
                      {p.nombre}
                    </Tooltip>
                  </Marker>
                );
              })}

            {showSingleFallbackMarker && (
              <Marker position={stopPosition} icon={stopIconSelected}>
                <Tooltip direction="top" opacity={0.92} offset={[0, -6]}>
                  {mapAnchor.nombre || 'Recorrido'}
                </Tooltip>
              </Marker>
            )}

            {ENABLE_LIVE_BUS_LOCATION &&
              liveBuses.map((bus) => (
                <Marker
                  key={`${bus.busId}-${String(bus.lastUpdate)}`}
                  position={[bus.lat, bus.lng]}
                  icon={busIcon}
                  zIndexOffset={isCatalogRecorrido ? 1200 : 900}
                >
                  <Tooltip direction="top" opacity={0.92} offset={[0, -8]}>
                    {`${bus.busId}${
                      typeof bus.velocidad === 'number' && bus.velocidad > 0
                        ? ` · ${Math.round(bus.velocidad)} km/h`
                        : ''
                    }`}
                  </Tooltip>
                </Marker>
              ))}

            {userLocation && userAccuracyM != null && userAccuracyM > 0 && userAccuracyM < 500 && (
              <Circle
                center={userLocation}
                radius={Math.min(Math.max(userAccuracyM, 15), 250)}
                pathOptions={{
                  color: 'hsl(4, 90%, 48%)',
                  fillColor: 'hsl(4, 90%, 58%)',
                  fillOpacity: 0.1,
                  weight: 1,
                }}
              />
            )}
            {userLocation && <Marker position={userLocation} icon={userIcon} />}
          </MapContainer>
          {servicioReemplazoExterno && (
            <div
              className="pointer-events-auto absolute inset-0 z-[2000] flex items-center justify-center bg-black/60 px-4 text-center shadow-inner backdrop-blur-[1px]"
              role="status"
              aria-live="polite"
              data-testid="map-servicio-reemplazo-overlay"
            >
              <p className="max-w-[22rem] text-sm font-semibold leading-snug text-white sm:text-base">
                {MENSAJE_BUS_REEMPLAZO_SIN_TIEMPO_REAL}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-3 bg-surface-container-low/50 px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 text-sm">
            {ENABLE_LIVE_BUS_LOCATION ? (
              <>
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <div
                    className={`mt-0.5 h-3 w-3 shrink-0 rounded-full ${
                      servicioReemplazoExterno
                        ? 'bg-muted-foreground/50'
                        : liveBuses.length > 0
                          ? 'bg-success'
                          : 'bg-muted-foreground/40'
                    }`}
                  />
                  <span className="font-medium leading-snug">
                    {servicioReemplazoExterno
                      ? MENSAJE_BUS_REEMPLAZO_SIN_TIEMPO_REAL
                      : liveBuses.length > 0
                        ? 'Bus del recorrido en mapa'
                        : 'Sin posición GPS del bus de este tramo (radio o fuera de cobertura)'}
                  </span>
                </div>
                {targetBusPatente && !servicioReemplazoExterno && (
                  <span className="shrink-0 text-right text-sm font-semibold tabular-nums text-foreground">
                    Patente: {targetBusPatente}
                  </span>
                )}
              </>
            ) : (
              <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 shrink-0 rounded-full bg-muted-foreground/40" />
                  <span className="font-medium">
                    Buses en vivo desactivados (REACT_APP_ENABLE_LIVE_BUS_LOCATION).
                  </span>
                </div>
              </div>
            )}
          </div>

          <div
            className="rounded-2xl bg-muted/40 px-3 py-3"
            style={
              {
                '--map-line-ida': mapLineColors.ida,
                '--map-line-vuelta': mapLineColors.vuelta,
              }
            }
          >
            <p className="mb-2 text-xs font-medium text-muted-foreground">Sentido del trazado</p>
            {hasKml ? (
              <>
                <ToggleGroup
                  type="single"
                  value={sentido}
                  onValueChange={(v) => {
                    if (v === 'ida' || v === 'vuelta') onSentidoChange(v);
                  }}
                  className="grid w-full grid-cols-2 gap-2"
                  aria-label="Elegir ida o vuelta"
                >
                  <ToggleGroupItem
                    value="ida"
                    className="h-11 rounded-xl border border-border/60 font-semibold data-[state=on]:border-transparent data-[state=on]:bg-[var(--map-line-ida)] data-[state=on]:text-white"
                    type="button"
                  >
                    <span
                      className="mr-2 inline-block h-1.5 w-6 rounded-full"
                      style={{ backgroundColor: mapLineColors.ida }}
                      aria-hidden
                    />
                    Ida
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="vuelta"
                    className="h-11 rounded-xl border border-border/60 font-semibold data-[state=on]:border-transparent data-[state=on]:bg-[var(--map-line-vuelta)] data-[state=on]:text-slate-900"
                    type="button"
                  >
                    <span
                      className="mr-2 inline-block h-1.5 w-6 rounded-full"
                      style={{ backgroundColor: mapLineColors.vuelta }}
                      aria-hidden
                    />
                    Vuelta
                  </ToggleGroupItem>
                </ToggleGroup>
                {variantOptions.length > 1 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {variantOptions.map((opt) => {
                      const isActive = (selectedVariantId || variantOptions[0]?.id) === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setSelectedVariantId(opt.id)}
                          className={`rounded-full px-3 py-1.5 text-[11px] font-bold tracking-wide transition-colors ${
                            isActive
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-card text-muted-foreground hover:bg-muted'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {kmlIvLoading && (
                  <p className="mt-2 text-[11px] text-muted-foreground">Cargando trazado…</p>
                )}
                {kmlIvError && (
                  <p className="mt-2 text-[11px] text-destructive">{kmlIvError}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                El trazado en mapa para este recorrido estará disponible próximamente.
              </p>
            )}
          </div>

          {prediccion && (
            <p className="mt-2 font-mono text-xs text-muted-foreground">
              Distancia: {prediccion.distanciaKm} km • ETA: {prediccion.etaMin} min
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
